import { useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import { Card } from './Card';
import { Kbd } from './Kbd';
import { useFocusTrap } from '../hooks/use-focus-trap';

interface Binding { keys: string[]; label: string; }

const BINDINGS: Binding[] = [
  { keys: ['g', 'h'], label: 'Go to hosts' },
  { keys: ['c'],      label: 'New host' },
  { keys: ['r'],      label: 'Refresh' },
  { keys: ['/'],      label: 'Focus filter' },
  { keys: ['?'],      label: 'Show this overlay' },
  { keys: ['Esc'],    label: 'Close any dialog/overlay' },
];

interface Props {
  open: boolean;
  onClose: () => void;
}

export function ShortcutOverlay({ open, onClose }: Props) {
  const ref = useRef<HTMLDivElement>(null);
  useFocusTrap(ref, open);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Keyboard shortcuts"
      className="fixed inset-0 z-50 grid place-items-center bg-black/70 p-4 backdrop-blur-sm"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <Card ref={ref} variant="elevated" className="w-full max-w-md p-6">
        <div className="flex items-start justify-between">
          <h2 className="font-[var(--font-serif)] text-xl font-bold italic text-text-primary">
            Keyboard shortcuts
          </h2>
          <button
            onClick={onClose}
            aria-label="Close"
            className="rounded-[var(--radius-sm)] p-1 text-text-muted hover:text-text-primary"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <ul className="mt-5 space-y-2.5">
          {BINDINGS.map((b) => (
            <li key={b.label} className="flex items-center justify-between gap-4">
              <span className="text-sm text-text-secondary">{b.label}</span>
              <span className="flex items-center gap-1">
                {b.keys.map((k, i) => (
                  <Kbd key={i}>{k}</Kbd>
                ))}
              </span>
            </li>
          ))}
        </ul>
      </Card>
    </div>
  );
}
